import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, Spinner } from 'reactstrap';
import { api } from '../../config';
import Chart from 'react-apexcharts';
import { CSVLink } from 'react-csv';
import FeatherIcon from 'feather-icons-react';

const BarChart = ({ token }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const res = await fetch(`${api.API_URL}/dashboard/get-by-product`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          throw new Error('Failed to fetch data');
        }

        const result = await res.json();
        setData(result);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    if (token) {
      fetchData();
    }
  }, [token]);

  const groupedData = data.reduce((acc, item) => {
    const name = item.productName?.[0] || 'Unknown';
    if (!acc[name]) {
      acc[name] = 0;
    }
    acc[name] += item.totalProcurements || 0;
    return acc;
  }, {});

  const categories = Object.keys(groupedData);
  const seriesData = Object.values(groupedData);

  const options = {
    chart: {
      type: 'bar',
      height: 350,
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
      },
    },
    xaxis: {
      categories: categories,
      labels: {
        rotate: -45,
        style: {
          fontSize: '12px',
          fontWeight: 500,
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          fontSize: '12px',
          fontWeight: 500,
        },
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: true,
        columnWidth: '50%',
        dataLabels: {
          position: 'center',
        },
      },
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: '12px',
        fontWeight: '700',
        colors: ['#ffffff'],
      },
      dropShadow: {
        enabled: false,
      },
    },
    colors: ['#0d6efd'],
    grid: {
      borderColor: '#f1f1f1',
    },
  };

  const series = [
    {
      name: 'Total Procurements',
      data: seriesData,
    },
  ];

  if (loading) {
    return (
      <Card className='border-0 shadow-sm h-100'>
        <CardBody
          className='d-flex justify-content-center align-items-center'
          style={{ minHeight: '350px' }}
        >
          <Spinner color='primary' />
        </CardBody>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className='border-0 shadow-sm h-100'>
        <CardBody
          className='d-flex justify-content-center align-items-center'
          style={{ minHeight: '350px' }}
        >
          <p className='text-danger mb-0'>Error: {error}</p>
        </CardBody>
      </Card>
    );
  }

  const csvData = Object.entries(groupedData).map(([product, total]) => ({
    'Product Name': product,
    'Total Procurements': total,
  }));

  return (
    <Card className='border-0 shadow-sm rounded-3'>
      <CardHeader className='border-0 bg-transparent py-3 px-4 d-flex align-items-center justify-content-between'>
        <div>
          <h4 className='card-title mb-1 fw-semibold'>
            Procurement by Product
          </h4>
          <p className='text-muted mb-0 fs-13'>
            Produk procurement terkini dalam satu grafik.
          </p>
        </div>
        <CSVLink
          data={csvData}
          filename='procurements-by-product.csv'
          className='btn btn-sm btn-soft-primary d-inline-flex align-items-center gap-2'
        >
          <FeatherIcon icon='download' size={14} />
          Export CSV
        </CSVLink>
      </CardHeader>
      <CardBody className='p-4'>
        <Chart options={options} series={series} type='bar' height={350} />
      </CardBody>
    </Card>
  );
};

export default BarChart;
